import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Sun, Moon } from "lucide-react";
import api from "../lib/api";
import { slotClass } from "../lib/slotColors";

const VISIBLE_DAYS = 4;

// Local-date YYYY-MM-DD — do NOT use toISOString() which returns UTC and shifts the day by ±1 near midnight.
function fmtDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function to12h(t) {
    const [h, m] = t.split(":").map(Number);
    const period = h < 12 ? "AM" : "PM";
    const hh = h === 0 ? 12 : (h > 12 ? h - 12 : h);
    return `${String(hh).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
}

function dateLabel(dStr) {
    const d = new Date(dStr + "T00:00:00");
    return {
        day: d.getDate(),
        weekday: d.toLocaleDateString("en", { weekday: "short" }),
        month: d.toLocaleDateString("en", { month: "short" }),
    };
}

/**
 * Multi-date × time slot grid.
 * Props:
 *   centreId, facilityId — slot source
 *   selectedDate         — currently selected date (legacy single-date mode) [optional]
 *   selectedTimes        — string[] of selected slot times for the active date [optional]
 *   onSelectionChange(date, times)  — single-date callback
 *   multiSelectMode      — when true, supports multi-date+multi-time selection
 *   selectedCells        — Set<string> of "date|time" keys (used in multiSelectMode)
 *   onCellsChange(setOfCellKeys)
 *   onBookedClick(booking, date, time) — admin tool: clicked an existing booking
 *   onBlockedClick(block, date, time)  — admin tool: clicked an existing block (to unblock/make-available)
 *   refreshKey           — bump to force re-fetch
 */
export default function MultiDateSlotGrid({
    centreId, facilityId,
    selectedDate, selectedTimes, onSelectionChange,
    multiSelectMode = false, selectedCells, onCellsChange,
    onBookedClick, onBlockedClick,
    refreshKey = 0,
    showExternalOrange = false,
    onPriceSum,          // Iter 40 — (sumOfSlotPrices: number) => void, recomputes on selection changes
}) {
    // Iter 46 — anchor the visible window on the incoming selectedDate (e.g. Modify
    // Booking opens on the BOOKING's own date) instead of always starting at today.
    // Previously the grid opened on today, so an admin clicking a slot on the "today"
    // column silently moved the booking to today via onSelectionChange(date, [time]).
    const [pageStart, setPageStart] = useState(() => {
        const today = fmtDate(new Date());
        if (selectedDate && selectedDate >= today) return selectedDate;
        return today;
    });
    const [grid, setGrid] = useState({});  // { dateStr: { half_hour_price, slots: [{time, booked, is_block}] } }
    const [loading, setLoading] = useState(false);
    const todayStr = fmtDate(new Date());
    const nowMin = new Date().getHours() * 60 + new Date().getMinutes();

    // Past-time helper: for today only, slots whose start-time is already in the past are unbookable.
    function isPastSlot(date, time) {
        if (date !== todayStr) return false;
        const [hh, mm] = time.split(":").map(Number);
        return (hh * 60 + mm) <= nowMin;
    }
    function isPastDate(date) {
        return date < todayStr;
    }

    const dates = useMemo(() => {
        const arr = [];
        const start = new Date(pageStart + "T00:00:00");
        for (let i = 0; i < VISIBLE_DAYS; i++) {
            const d = new Date(start); d.setDate(start.getDate() + i);
            arr.push(fmtDate(d));
        }
        return arr;
    }, [pageStart]);

    // Slot times derived from any loaded date
    const timeRows = useMemo(() => {
        for (const d of dates) {
            if (grid[d]?.slots?.length) return grid[d].slots.map(s => s.time);
        }
        return [];
    }, [grid, dates]);

    // Iter 40 — CRITICAL PRICING FIX: emit the ACTUAL sum of the selected slots' individual
    // prices (respecting admin per-slot overrides + peak pricing) so the modal shows the
    // correct total. Was previously `N × halfPrice` which ignored overrides.
    useEffect(() => {
        if (!onPriceSum) return;
        let sum = 0;
        if (multiSelectMode && selectedCells) {
            for (const key of selectedCells) {
                const [d, t] = key.split("|");
                const dayData = grid[d];
                if (!dayData) continue;
                const slot = (dayData.slots || []).find(s => s.time === t);
                if (slot) sum += Number(slot.price ?? dayData.half_hour_price ?? 0);
            }
        } else if (selectedDate && selectedTimes && selectedTimes.length) {
            const dayData = grid[selectedDate];
            if (dayData) {
                for (const t of selectedTimes) {
                    const slot = (dayData.slots || []).find(s => s.time === t);
                    if (slot) sum += Number(slot.price ?? dayData.half_hour_price ?? 0);
                }
            }
        }
        onPriceSum(sum);
    }, [grid, selectedCells, selectedTimes, selectedDate, multiSelectMode, onPriceSum]);

    useEffect(() => {
        if (!centreId || !facilityId) return;
        setLoading(true);
        Promise.all(dates.map(d =>
            api.get("/slots", { params: { centre_id: centreId, facility_id: facilityId, booking_date: d } })
                .then(r => [d, r.data])
                .catch(() => [d, null])
        )).then(pairs => {
            const next = {};
            for (const [d, data] of pairs) if (data) next[d] = data;
            setGrid(next);
            setLoading(false);
        });
    }, [centreId, facilityId, pageStart, refreshKey]); // eslint-disable-line

    const onCellClick = (date, time, booked, isBlock, slot) => {
        // Prevent selecting/booking past dates entirely (admin tools may still inspect bookings)
        if (isPastDate(date) && !booked && !isBlock) {
            return; // silently — past free cells are non-interactive
        }
        if (isPastSlot(date, time) && !booked && !isBlock) {
            return; // past time on today
        }
        // Admin tool: clicked an existing block → offer "Make Available"
        if (isBlock && onBlockedClick) {
            onBlockedClick(slot?.block || null, date, time);
            return;
        }
        if (booked && !isBlock) {
            // Surface the click upward so admin tools can offer Cancel/Modify
            if (slot?.booking && onBookedClick) onBookedClick(slot.booking, date, time);
            return;
        }
        if (multiSelectMode) {
            const key = `${date}|${time}`;
            const next = new Set(selectedCells || []);
            if (next.has(key)) next.delete(key); else next.add(key);
            onCellsChange?.(next);
            return;
        }
        if (date !== selectedDate) {
            onSelectionChange(date, [time]);
            return;
        }
        const has = selectedTimes.includes(time);
        onSelectionChange(date, has ? selectedTimes.filter(t => t !== time) : [...selectedTimes, time]);
    };

    const shiftPage = (delta) => {
        const start = new Date(pageStart + "T00:00:00");
        start.setDate(start.getDate() + delta * VISIBLE_DAYS);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        if (start < today) start.setTime(today.getTime());
        setPageStart(fmtDate(start));
    };

    const halfPrice = useMemo(() => {
        for (const d of dates) {
            if (grid[d]?.half_hour_price) return grid[d].half_hour_price;
        }
        return 0;
    }, [grid, dates]);

    return (
        <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden" data-testid="multi-date-slot-grid">
            {/* Header row: time-spacer + 4 date headers + paging */}
            <div className="grid items-center bg-slate-950/60 border-b border-slate-800" style={{ gridTemplateColumns: "84px repeat(4, 1fr)" }}>
                <div className="px-3 py-3 text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">
                    {loading ? "…" : "Time"}
                </div>
                {dates.map(d => {
                    const { day, weekday, month } = dateLabel(d);
                    const isActive = d === selectedDate;
                    const isToday = d === todayStr;
                    return (
                        <div key={d} data-testid={`grid-date-${d}`}
                            className={`text-center py-3 border-l border-slate-800 ${isActive ? "bg-[#00FF66]/10" : isToday ? "bg-[#00FF66]/5" : ""}`}>
                            <div className="font-display text-xl font-bold text-white leading-none">{day}</div>
                            <div className={`text-[10px] uppercase tracking-widest mt-1 ${isActive || isToday ? "text-[#00FF66]" : "text-slate-400"}`}>{month}</div>
                            <div className={`text-[10px] uppercase tracking-widest mt-0.5 ${isActive ? "text-[#00FF66]" : isToday ? "text-[#00FF66]/80" : "text-slate-500"}`}>
                                {isToday ? "Today" : weekday}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Body */}
            <div className="max-h-[60vh] overflow-y-auto">
                {timeRows.length === 0 && !loading && (
                    <div className="text-center text-slate-500 text-sm py-10">No slots for the selected facility.</div>
                )}
                {timeRows.map((t) => {
                    const [hh] = t.split(":").map(Number);
                    const isMorning = hh < 12;
                    return (
                        <div key={t} className="grid border-b border-slate-800/70" style={{ gridTemplateColumns: "84px repeat(4, 1fr)" }}>
                            <div className="px-3 py-2 flex items-start gap-1 text-slate-300">
                                <div className="leading-tight">
                                    <div className="text-xs font-bold tracking-wide">{to12h(t)}</div>
                                    <div className="mt-0.5 text-slate-500">
                                        {isMorning
                                            ? <Sun className="w-3 h-3 text-amber-400/80" />
                                            : <Moon className="w-3 h-3 text-indigo-300/80" />}
                                    </div>
                                </div>
                            </div>
                            {dates.map((d) => {
                                const dayData = grid[d];
                                const slot = dayData?.slots?.find(s => s.time === t);
                                if (!slot) {
                                    return <div key={d} className="border-l border-slate-800/70 bg-slate-900/40" />;
                                }
                                const isSel = multiSelectMode
                                    ? !!(selectedCells && selectedCells.has(`${d}|${t}`))
                                    : (d === selectedDate && selectedTimes.includes(t));
                                const isExternal = showExternalOrange && !!(slot.booking && (slot.booking.external_source || slot.booking.source === "external"));
                                const cls = slotClass({ booked: slot.booked && !slot.is_block, isBlock: slot.is_block, selected: isSel, external: isExternal });
                                const price = dayData?.half_hour_price ?? halfPrice;
                                const blockClickable = slot.is_block && onBlockedClick;
                                const bookedClickable = slot.booked && !slot.is_block && onBookedClick;
                                const past = (isPastDate(d) || isPastSlot(d, t)) && !slot.booked && !slot.is_block;
                                return (
                                    <button key={d}
                                        type="button"
                                        onClick={() => onCellClick(d, t, slot.booked, slot.is_block, slot)}
                                        disabled={(slot.is_block && !blockClickable) || past}
                                        data-testid={`grid-cell-${d}-${t}`}
                                        title={past ? "Slot has passed" : (blockClickable ? "Click to make available" : "")}
                                        className={`m-1.5 rounded border ${past ? "bg-slate-900 border-slate-800 text-slate-700 cursor-not-allowed line-through opacity-50" : cls} text-xs font-bold py-3 transition-all ${
                                            (bookedClickable || blockClickable) && !past ? "cursor-pointer hover:ring-2 hover:ring-[#00FF66]/60" : ""
                                        }`}>
                                        {past
                                            ? <span>Past</span>
                                            : slot.is_block
                                                ? <span className="opacity-80">Blocked</span>
                                                : slot.booked
                                                    ? <span>{isExternal ? "Other App" : "Booked"}</span>
                                                    : <span>₹{(slot.price ?? price).toLocaleString("en-IN")}</span>}
                                    </button>
                                );
                            })}
                        </div>
                    );
                })}
            </div>

            {/* Footer: pagination */}
            <div className="flex items-center justify-between px-4 py-2 bg-slate-950/60 border-t border-slate-800">
                <button type="button" onClick={() => shiftPage(-1)} data-testid="grid-prev-btn"
                    className="inline-flex items-center gap-1 px-3 h-8 text-[10px] font-bold uppercase tracking-widest text-slate-300 hover:text-white">
                    <ChevronLeft className="w-4 h-4" /> Previous
                </button>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">
                    {dateLabel(dates[0]).day} {new Date(dates[0]).toLocaleDateString("en", { month: "short" })} – {dateLabel(dates[VISIBLE_DAYS - 1]).day} {new Date(dates[VISIBLE_DAYS - 1]).toLocaleDateString("en", { month: "short" })}
                </div>
                <button type="button" onClick={() => shiftPage(1)} data-testid="grid-next-btn"
                    className="inline-flex items-center gap-1 px-3 h-8 text-[10px] font-bold uppercase tracking-widest text-slate-300 hover:text-white">
                    Next <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
